import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { AccountInfo } from '@cef-ai/wallet-identity';
import { Button, Card } from '~/ui/primitives';
import { EmailBindFlow } from '~/routes/Settings/EmailBindFlow';

/**
 * The account's own identity — display name and verified email — as a
 * persistent card, distinct from `EmailNudge`'s transient prompt.
 *
 * Until this existed there was nowhere in the wallet that showed the bound
 * address (CEF-AI/wallet#73): `EmailNudge` is the only component that ever
 * rendered one, and it hides itself precisely when the account has one. That
 * left the recovery handle — the address `email/verify` proved, the one a
 * later `already-registered` answer refers to, the one `EmailBindFlow`
 * silently REPLACES — unreadable by the person it belongs to.
 *
 * Nothing is fetched here. `identity.account` is populated by every path that
 * establishes a session, bearer-resume included, so this reads state that is
 * already in the tab.
 */
// `account` is optional on the `Identity` interface, so `undefined` (an
// implementation that carries no account at all) reaches here as well as
// `null` (signed out / not yet arrived). All three non-account cases render
// the same nothing.
export const AccountIdentity = observer(({ account }: { account: AccountInfo | null | undefined }) => {
  const [binding, setBinding] = useState(false);
  const [justBound, setJustBound] = useState<string | null>(null);

  // No account yet (a session whose account fields have not landed). Render
  // nothing rather than an empty card asserting the account has no email —
  // "not known yet" and "not set" are different, and only one of them should
  // offer to bind an address.
  if (!account) return null;

  const email = justBound ?? account.email;

  if (binding) {
    return (
      <Card>
        <EmailBindFlow
          onBound={(bound) => {
            // Held locally as well as pushed into `identity.account` by the
            // flow itself: this card must show the new address on the very
            // next render, not whenever the account object happens to update.
            setJustBound(bound);
            setBinding(false);
          }}
          onCancel={() => setBinding(false)}
        />
      </Card>
    );
  }

  return (
    <Card>
      {/* No card heading. The page is already titled "Account" and the nav
          item says it too; a third "Account" above the fold labels nothing.
          The rows carry their own labels, and the addresses card below is
          unheaded for the same reason. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Row label="Name" value={account.displayName ?? '—'} />
        {email != null ? (
          <Row label="Email" value={email} action={{ label: 'Change', onClick: () => setBinding(true) }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <span style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--text-muted)' }}>Email</span>
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              No address on this account. Adding one is how you would recover this wallet on another device.
            </p>
            <div>
              <Button variant="secondary" size="sm" fullWidth={false} onClick={() => setBinding(true)}>
                Add your email
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
});

function Row({
  label,
  value,
  action,
}: {
  label: string;
  value: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <span style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--text-muted)' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <span style={{ font: '500 15px/1.4 var(--font-sans)', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
          {value}
        </span>
        {action && (
          <Button variant="tertiary" size="sm" fullWidth={false} onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </div>
    </div>
  );
}
